import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import Login from "./routes/Login";
import OAuthGmail from "./routes/OAuthGmail";
import Home from "./routes/Home";
import CreateBox from "./routes/CreateBox";
import BoxDetail from "./routes/BoxDetail";
import Review from "./routes/Review";
import Header from "./components/Header";
import MainContext from "./context/mainContext";
import { useContext } from "react";
import { getAuthUserinfoApi, loginLeitnerpwaApi } from "./services/authApi";
import { getBoxesListApi } from "./services/leitnerApi";
import { checkErrorMessage, checkResponseData } from "./utils/utilsFunctions";
import { decodeToken } from "./utils/premiumCheck";
import { refreshToken, setOnSubscriptionRequired } from "./services/apiClient";
import WordTransfer from "./routes/WordTransfer";
import { PackagesModalProvider, usePackagesModal } from "./context/PackagesModalContext";
import PackagesModal from "./components/PackagesModal/PackagesModal";

function PrivateRoute({ children }) {
  const { isAuthed } = useContext(MainContext);
  return isAuthed ? children : <Navigate to="/login" replace />;
}

export default function App() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const [firstLoad, setFirstLoad] = useState(true);
  const [loadingText, setLoadingText] = useState("");
  const [isAuthed, setIsAuthed] = useState(false);
  const [boxesList, setBoxesList] = useState([]);
  const [cards, setCards] = useState({
    one: [], two: [], three: [], four: [], five: [],
    six: [], seven: [], eight: [], nine: [],
  });
  const [userTypes, setUserTypes] = useState([]);

  const handleCards = (value) => setCards(value);
  const handleIsAuthed = (value) => setIsAuthed(value);
  const handleEditBoxlist = (data) => setBoxesList(data);
  const handleUserType = (data) => setUserTypes(data);

  const getBoxListInFirstLoad = async () => {
    setLoadingText("در حال دریافت اطلاعات لایتنر");
    try {
      const getBoxesList = await getBoxesListApi({});
      const resData = checkResponseData(getBoxesList);
      if (resData["success"]) {
        setBoxesList(resData["data"]["docs"]);
      } else {
        setBoxesList([]);
      }
    } catch (error) {
      console.log("getBoxListInFirstLoad", error);
    }
  };

  const loadUserTypesFromToken = () => {
    const token = localStorage.getItem("token");
    const payload = decodeToken(token);
    if (payload?.type) {
      handleUserType(payload.type);
    }
  };

  const checkUserLogin = async (token, tokenLogin) => {
    try {
      if (tokenLogin) {
        setLoadingText("در حال بررسی اعتبارسنجی...");
        const response = await loginLeitnerpwaApi(tokenLogin);
        const resData = checkResponseData(response);
        if (resData["success"]) {
          localStorage.setItem("token", response.data.token);
          localStorage.setItem("refreshToken", response.data.refreshToken);
          loadUserTypesFromToken();
          handleIsAuthed(true);
          await getBoxListInFirstLoad();
        } else if (token) {
          await tryTokenLogin(token);
        } else {
          handleIsAuthed(false);
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("token");
        }
      } else if (token) {
        await tryTokenLogin(token);
      }
    } catch (error) {
      handleIsAuthed(false);
    }
  };

  const tryTokenLogin = async (token) => {
    try {
      loadUserTypesFromToken();
      handleIsAuthed(true);
      await getBoxListInFirstLoad();
    } catch {
      handleIsAuthed(false);
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("token");
    }
  };

  const tryRefreshTokenOnLoad = async () => {
    const refreshTokenVal = localStorage.getItem("refreshToken");
    if (refreshTokenVal) {
      try {
        setLoadingText("بروزرسانی نشست...");
        const success = await refreshToken();
        if (success) {
          loadUserTypesFromToken();
        }
        return success;
      } catch {
        return false;
      }
    }
    return false;
  };

  useEffect(() => {
    const initApp = async () => {
      setFirstLoad(true);
      const tokenLogin = queryParams.get("token");
      const token = localStorage.getItem("token");

      if (token || tokenLogin) {
        await tryRefreshTokenOnLoad();
        await checkUserLogin(localStorage.getItem("token"), tokenLogin);
      }
      setFirstLoad(false);
    };
    initApp();
  }, []);

  return (
    <MainContext.Provider
      value={{
        isAuthed,
        handleEditBoxlist,
        userTypes,
        handleUserType,
        boxesList,
        handleIsAuthed,
        cards,
        handleCards,
      }}
    >
      <PackagesModalProvider>
        <AppContent
          firstLoad={firstLoad}
          loadingText={loadingText}
          isAuthed={isAuthed}
          getBoxListInFirstLoad={getBoxListInFirstLoad}
        />
      </PackagesModalProvider>
    </MainContext.Provider>
  );
}

function AppContent({ firstLoad, loadingText, isAuthed, getBoxListInFirstLoad }) {
  const { showPackages } = usePackagesModal();

  useEffect(() => {
    setOnSubscriptionRequired(() => showPackages());
    return () => setOnSubscriptionRequired(null);
  }, [showPackages]);

  return (
    <>
      {firstLoad ? (
        <div className="loader-overlay">
          <div className="spinner"></div>
          <p className="loader-text">{loadingText}</p>
        </div>
      ) : (
        <div className="app">
          <Header />
          <main className="main-content">
            <Routes>
              <Route
                path="/"
                element={<Navigate to={isAuthed ? "/home" : "/login"} replace />}
              />
              <Route
                path="/login"
                element={<Login getBoxListInFirstLoad={getBoxListInFirstLoad} />}
              />
              <Route
                path="/oauthGmail"
                element={<OAuthGmail />}
              />
              <Route
                path="/home"
                element={<PrivateRoute><Home /></PrivateRoute>}
              />
              <Route
                path="/create"
                element={<PrivateRoute><CreateBox /></PrivateRoute>}
              />
              <Route
                path="/wordtransfer"
                element={<PrivateRoute><WordTransfer /></PrivateRoute>}
              />
              <Route
                path="/box/:id"
                element={<PrivateRoute><BoxDetail /></PrivateRoute>}
              />
              <Route
                path="/review/:id"
                element={<PrivateRoute><Review /></PrivateRoute>}
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
          <PackagesModalWrapper />
        </div>
      )}
    </>
  );
}

function PackagesModalWrapper() {
  const { isOpen } = usePackagesModal();
  return isOpen ? <PackagesModal /> : null;
}
